import { useNavigateWithSearch } from 'Modulor/hooks';
import type { FC } from 'react';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import { FlowTypes } from 'store/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { PayHubCashierContractsReversePendingWithdrawalV2Item } from 'store/models';
import { useReversePendingWithdrawalV2Mutation } from 'store/services/withdrawalRejectAPI';
import {
  pendingWithdrawalsSelector,
  preselectedOptionSelector,
  reversePrecisionSelector,
  setCloseTrigger,
} from 'store/slices/withdrawalReject';

import { Routes } from '../../../routes';
import { PartialReverse } from './PartialReverse';
import { PartialReverseResult } from './PartialReverseResult';

const clickstream = getEventAnalytic();

interface PartialReverseContainerProps {
  onDeposit: () => void;
}

export const PartialReverseContainer: FC<PartialReverseContainerProps> = ({ onDeposit }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigateWithSearch();
  const cashierContext = useGetClickStreamCashierContext(FlowTypes.withdrawal);
  const pendingWithdrawals = useAppSelector(pendingWithdrawalsSelector);
  const preselectedOption = useAppSelector(preselectedOptionSelector);
  const precision = useAppSelector(reversePrecisionSelector);
  const [reverse, { data, isLoading }] = useReversePendingWithdrawalV2Mutation();

  const handleReturn = async (items: PayHubCashierContractsReversePendingWithdrawalV2Item[]) => {
    // TODO(clickstream): extend with option/return_amount/remain_amount/transaction_count once schemas are released
    clickstream.push(getClickStreamEvent.cashier_homepage_popup_cancel_withdrawal_confirm_button_click, [
      cashierContext,
    ]);

    const response = await reverse({ items });

    // TODO(PM): mixed result (some merchant_declined/failed) is shown as success with totals of reversed items
    if ('error' in response || !response.data.success_amount) {
      dispatch(setCloseTrigger());
      navigate(Routes.depositError);
    }
  };

  if (data?.success_amount) {
    return <PartialReverseResult result={data} onDeposit={onDeposit} />;
  }

  return (
    <PartialReverse
      withdrawals={pendingWithdrawals}
      preselectedOption={preselectedOption}
      precision={precision}
      isFetching={isLoading}
      onReturn={handleReturn}
      onDeposit={onDeposit}
    />
  );
};
